import type { StrapiMedia } from "@shared/content-types/schemas/media.schema";
import type { Seo } from "@shared/content-types/schemas/seo.schema";

export interface SeoConfig
  extends Partial<Omit<Seo, "title" | "description" | "ogImage">> {
  title?: string | null;
  description?: string | null;
  ogImage?: StrapiMedia | null;
}

/**
 * Поля SEO из Strapi, которые конфиг не переопределяет: keywords, schemaOrg,
 * id. Раньше конфиг собирался только из заголовка, описания и картинки, и
 * ключевые слова и разметка страницы молча терялись
 */
const pageSeo = (pageData: Record<string, unknown>): Partial<Seo> =>
  (pageData.seo as Partial<Seo> | null | undefined) ?? {};

// Predefined configurations for common page types
export const getSeoConfig = {
  article: (pageData: Record<string, unknown>): SeoConfig => ({
    ...pageSeo(pageData),
    title:
      (pageData.seo as Partial<Seo>)?.title ||
      (pageData.title as string) ||
      null,
    description:
      (pageData.seo as Partial<Seo>)?.description ||
      ((pageData.hero as Record<string, unknown>)?.description as string) ||
      null,
    ogImage:
      (pageData.seo as Partial<Seo>)?.ogImage ||
      ((pageData.hero as Record<string, unknown>)?.mainImage as StrapiMedia) ||
      null,
  }),

  culturalObject: (pageData: Record<string, unknown>): SeoConfig => ({
    ...pageSeo(pageData),
    title:
      (pageData.seo as Partial<Seo>)?.title ||
      (pageData.title as string) ||
      null,
    description:
      (pageData.seo as Partial<Seo>)?.description ||
      (pageData.description as string) ||
      null,
    ogImage:
      (pageData.seo as Partial<Seo>)?.ogImage ||
      (pageData.backgroundImage as StrapiMedia) ||
      null,
  }),

  document: (pageData: Record<string, unknown>): SeoConfig => ({
    ...pageSeo(pageData),
    title:
      (pageData.seo as Partial<Seo>)?.title ||
      (pageData.title as string) ||
      null,
    description: (pageData.seo as Partial<Seo>)?.description || null,
    ogImage: (pageData.seo as Partial<Seo>)?.ogImage || null,
  }),

  service: (pageData: Record<string, unknown>): SeoConfig => ({
    ...pageSeo(pageData),
    title:
      (pageData.seo as Partial<Seo>)?.title ||
      (pageData.title as string) ||
      null,
    description: (pageData.seo as Partial<Seo>)?.description || null,
    ogImage:
      (pageData.seo as Partial<Seo>)?.ogImage ||
      (pageData.backgroundImage as StrapiMedia) ||
      null,
  }),

  trail: (pageData: Record<string, unknown>): SeoConfig => ({
    ...pageSeo(pageData),
    title:
      (pageData.seo as Partial<Seo>)?.title ||
      (pageData.title as string) ||
      null,
    description:
      (pageData.seo as Partial<Seo>)?.description ||
      (pageData.description as string) ||
      null,
    ogImage:
      (pageData.seo as Partial<Seo>)?.ogImage ||
      (pageData.image as StrapiMedia) ||
      null,
  }),

  cafesRestaurantsPoint: (pageData: Record<string, unknown>): SeoConfig => ({
    ...pageSeo(pageData),
    title:
      (pageData.seo as Partial<Seo>)?.title ||
      (pageData.title as string) ||
      null,
    description:
      (pageData.seo as Partial<Seo>)?.description ||
      (pageData.description as string) ||
      null,
    ogImage:
      (pageData.seo as Partial<Seo>)?.ogImage ||
      ((pageData.photos as StrapiMedia[])?.[0] as StrapiMedia) ||
      null,
  }),

  accommodationsPoint: (pageData: Record<string, unknown>): SeoConfig => ({
    ...pageSeo(pageData),
    title:
      (pageData.seo as Partial<Seo>)?.title ||
      (pageData.title as string) ||
      null,
    description:
      (pageData.seo as Partial<Seo>)?.description ||
      (pageData.description as string) ||
      null,
    ogImage:
      (pageData.seo as Partial<Seo>)?.ogImage ||
      ((pageData.photos as StrapiMedia[])?.[0] as StrapiMedia) ||
      null,
  }),
} as const;
